import React, { ReactElement, useMemo, useState } from 'react';
import { TableV2, TableV2Column } from 'components/Table/TableV2';
import { Box, Link, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import {
  GetUsersForOrganizationQuery,
  useGetUsersForOrganizationQuery,
  useRemoveUserFromOrganizationMutation,
  useResendUserInviteMutation,
  useUpdateUserOrganizationRoleMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { OrganizationGroupRole, UserRole } from 'generated-graphql/types';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import { Delete } from '@mui/icons-material';
import SendIcon from '@mui/icons-material/Send';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { ToastType, useToast } from 'services/toast';
import { useConfirmationModal } from 'services/modals/hooks';
import { useLoadingOverlay } from 'services/loading/hooks';
import {
  formatDateFromNowAbbreviated,
  isMoreThan7DaysPast
} from 'utils/datetime';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'services/user/atoms';
import { ConfirmationModalType } from 'services/modals/constants';
import { TextBlock } from 'common/ui/TextBlock';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';

const teamSectionHeaderContainer: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 4
};

const actionPopoverContainer: SxProps = {
  display: 'flex'
};

type Props = {
  title: string;
  subtitle?: string | ReactElement;
  organizationId: string;
};

const errorMessageElement = (message: string) => {
  return (
    <Typography variant="body2" mt={1}>
      {message}. Try again or{' '}
      <Link href="mailto:support@joinpopp.com?subject=Failed to manage team">
        contact us
      </Link>
    </Typography>
  );
};

type OrganizationUserItem = NonNullable<
  GetUsersForOrganizationQuery['getUsersForOrganization']
>['items'][number];

const UserAction = ({
  userData,
  userOrganizationId,
  onDeleteUserOrganization,
  onUpdateUserOrganization,
  onResendInvite
}: {
  userData: OrganizationUserItem;
  userOrganizationId: string;
  onDeleteUserOrganization: (
    userOrganizationId: string,
    isOnboard: boolean,
    name?: string
  ) => void;
  onUpdateUserOrganization: (
    userOrganizationId: string,
    role: UserRole,
    name: string
  ) => void;
  onResendInvite: (email: string) => void;
}) => {
  const user = useRecoilValue(userAtom);

  const onDeleteClick = () => {
    onDeleteUserOrganization(
      userOrganizationId,
      userData.isOnboard || false,
      userData.name || undefined
    );
  };

  const onSetAsAdminClick = () => {
    onUpdateUserOrganization(
      userOrganizationId,
      UserRole.Admin,
      userData.name || ''
    );
  };

  const onSetAsMemberClick = () => {
    onUpdateUserOrganization(
      userOrganizationId,
      UserRole.Member,
      userData.name || ''
    );
  };

  const onResendInviteClick = () => {
    if (userData.email) {
      onResendInvite(userData.email);
    }
  };

  const rowActionsOptions = useMemo<ActionOption[]>(() => {
    const options: ActionOption[] = [];
    const userRole = userData.role;

    // For invited users, only show "Resend invite" option
    if (!userData.isOnboard && !userData.lastLoginAt && userData.invitedDate) {
      options.unshift({
        label: 'Resend invite',
        onClick: onResendInviteClick,
        icon: <SendIcon />
      });
      return options;
    }

    // For onboard users, show Delete based on role/permissions
    if (
      userRole === UserRole.Member ||
      user?.organizationGroupRole ===
        OrganizationGroupRole.OrganizationGroupAdmin
    ) {
      options.push({
        label: 'Delete',
        onClick: onDeleteClick,
        icon: <Delete />,
        itemColor: 'error.main'
      });
    }

    if (userRole === UserRole.Member) {
      options.unshift({
        label: 'Set as admin',
        onClick: onSetAsAdminClick,
        icon: <AccountCircleIcon />
      });
    } else if (
      user?.organizationGroupRole ===
      OrganizationGroupRole.OrganizationGroupAdmin
    ) {
      options.unshift({
        label: 'Set as member',
        onClick: onSetAsMemberClick,
        icon: <AccountCircleIcon />
      });
    }

    return options;
  }, [
    userData,
    userData.role,
    userData.isOnboard,
    user?.organizationGroupRole
  ]);

  return (
    <Box sx={actionPopoverContainer}>
      {rowActionsOptions.length > 0 && (
        <ActionPopover id={userData.id} options={rowActionsOptions} />
      )}
    </Box>
  );
};

export const TeamSection: React.FC<Props> = ({
  title,
  subtitle,
  organizationId
}) => {
  const { showToast } = useToast();
  const { showModal } = useConfirmationModal();
  const { openDrawer } = useDrawer();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();

  const [users, setUsers] = useState<OrganizationUserItem[]>([]);
  const currentUser = useRecoilValue(userAtom);

  const { refetch, loading: isLoading } = useGetUsersForOrganizationQuery({
    variables: { organizationId },
    onCompleted: (result) => {
      const userList = extractQueryListItems(result).filter(
        (item) => item.id !== (currentUser?.id || '') && !item.isDeactivated
      );
      setUsers(userList);
    }
  });

  const refetchAndSetList = async () => {
    const usersResult = await refetch();

    const usersList = extractQueryListItems(usersResult.data).filter(
      (item) => item.id !== (currentUser?.id || '') && !item.isDeactivated
    );

    setUsers(usersList);
  };

  const [removeUserFromOrganizationMutation] =
    useRemoveUserFromOrganizationMutation();

  const [updateUserOrganizationRoleMutation] =
    useUpdateUserOrganizationRoleMutation();

  const [resendUserInviteMutation] = useResendUserInviteMutation();

  const handleDeleteUserOrganization = async (
    userOrganizationId: string,
    isOnboard: boolean,
    name?: string
  ) => {
    const message = isOnboard
      ? 'Ensure notifications are changed for any live campaigns. A reminder will be emailed to account admins.'
      : 'The invitation sent will no longer be valid.';

    showModal({
      title: 'Are you sure?',
      message,
      modalType: ConfirmationModalType.DESTRUCTIVE,
      cancelText: 'Cancel',
      destructiveText: 'Delete',
      onDestructiveConfirm: async () => {
        showLoadingOverlay();
        try {
          await removeUserFromOrganizationMutation({
            variables: {
              input: {
                userOrganizationId
              }
            }
          });

          await refetchAndSetList();

          if (isOnboard) {
            showToast({
              title: `${name} deleted`,
              type: ToastType.Success
            });
          } else {
            showToast({
              title: 'Deleted',
              message: 'The invitation sent will no longer be valid.',
              type: ToastType.Success
            });
          }
        } catch (e) {
          const errorMessage = isOnboard
            ? 'User could not be removed'
            : 'Invitation could not be deleted';

          showToast({
            title: 'Something went wrong',
            message: errorMessage,
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  const handleUpdateUserOrganization = async (
    userOrganizationId: string,
    role: UserRole,
    name: string
  ) => {
    showLoadingOverlay();

    try {
      await updateUserOrganizationRoleMutation({
        variables: {
          input: {
            userOrganizationId,
            userOrganizationRole: role
          }
        }
      });

      await refetchAndSetList();

      const title = `${name} is now ${
        role === UserRole.Admin ? 'an admin' : 'a member'
      }`;

      showToast({ title, type: ToastType.Success });
    } catch (e) {
      showToast({
        title: 'Something went wrong',
        message: errorMessageElement('Permissions could not be updated'),
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  const handleResendInvite = (email: string) => {
    showModal({
      title: 'Resend invitation?',
      message: 'The invitation will be valid for seven days.',
      confirmationText: 'Resend invitation',
      onConfirm: async () => {
        showLoadingOverlay();
        try {
          await resendUserInviteMutation({
            variables: {
              input: {
                email
              }
            }
          });

          await refetchAndSetList();

          showToast({
            title: 'Invitation resent',
            type: ToastType.Success
          });
        } catch (e) {
          showToast({
            title: 'Something went wrong',
            message: errorMessageElement('Invitation could not be resent'),
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  const columns: TableV2Column<OrganizationUserItem>[] = useMemo(
    () => [
      {
        name: 'Name',
        selector: (row) => row.name || '',
        sortable: true,
        sortField: 'name',
        minWidth: '260px'
      },
      {
        name: 'Last log in',
        selector: (row) =>
          row.lastLoginAt ? formatDateFromNowAbbreviated(row.lastLoginAt) : '-',
        sortable: true,
        sortField: 'lastLoginAt'
      },
      {
        name: 'Email',
        selector: (row) => row.email || '',
        cell: (row) =>
          row.email ? (
            <Link href={`mailto:${row.email}`}>{row.email}</Link>
          ) : (
            '-'
          ),
        minWidth: '260px'
      },
      {
        name: 'Role',
        selector: (row) =>
          row.organizationGroupRole ===
          OrganizationGroupRole.OrganizationGroupAdmin
            ? 'superadmin'
            : row.role?.toLowerCase() ?? '',
        style: { textTransform: 'capitalize' },
        sortable: true,
        sortField: 'role'
      },
      {
        name: 'Status',
        sortable: true,
        selector: (row) => {
          if (row.isOnboard || row.lastLoginAt) {
            return 'Active';
          } else if (row.invitedDate && isMoreThan7DaysPast(row.invitedDate)) {
            return 'Invite lapsed';
          } else {
            return 'Invited';
          }
        }
      },
      {
        name: 'Action',
        isAction: true,
        cell: (row) => {
          // Organization group admins ("superadmins") reach the org at the
          // group level and have no UserOrganization record to act on, so
          // their rows (no userOrganizationId) are read-only.
          if (!row.userOrganizationId) {
            return null;
          }

          return (
            <UserAction
              userData={row}
              userOrganizationId={row.userOrganizationId}
              onDeleteUserOrganization={handleDeleteUserOrganization}
              onUpdateUserOrganization={handleUpdateUserOrganization}
              onResendInvite={handleResendInvite}
            />
          );
        }
      }
    ],
    [organizationId]
  );

  return (
    <Box>
      <Box sx={teamSectionHeaderContainer}>
        <TextBlock
          variant="section"
          title={title}
          subtitle={
            subtitle || (
              <Typography>
                Invite and manage team’s permissions.{' '}
                <Link
                  href="#"
                  onClick={() =>
                    openDrawer({
                      drawerContentType:
                        DrawerComponentType.TeamPermissionsDrawer,
                      drawerComponentProps: {
                        hideCloseIconButton: true
                      }
                    })
                  }
                >
                  Learn about permissions
                </Link>
              </Typography>
            )
          }
        />
        <Button
          variant="primary"
          onClick={() =>
            openDrawer({
              drawerContentType: DrawerComponentType.InviteUserDrawer,
              drawerComponentProps: {
                organizationId,
                refetch: refetchAndSetList,
                hideCloseIconButton: true
              }
            })
          }
        >
          Add team member
        </Button>
      </Box>
      <TableV2
        data={users}
        columns={columns}
        keyField="id"
        pagination
        isLoading={isLoading}
      />
    </Box>
  );
};
